/**
 * Retrieves and processes return information from the CMS based on delivery key and category slugs.
 *
 * @async
 * @function useInfoReturn
 * @param {string} deliveryKey - The unique key used to retrieve the specific return information.
 * @param {string[]} categoriesSlugs - An array of category slugs to check for content visibility.
 * @returns {Promise<Object>} An object containing return information and visibility flags.
 *
 * @description
 * This composable function fetches return information from the CMS API with the following features:
 * - Retrieves content based on the provided delivery key
 * - Determines content visibility based on country and category
 * - Extracts title and visibility flags
 *
 * @example
 * // Fetch return information for a specific delivery key and categories
 * const { title, hideInCountry, hideInCategory } = await useInfoReturn(
 *   'return_policy',
 *   ['electronics', 'clothing']
 * )
 *
 * @property {string} title - The title of the return information.
 * @property {boolean} hideInCountry - Indicates if the content should be hidden in the current country.
 * @property {boolean} hideInCategory - Indicates if the content should be hidden in the given categories.
 *
 * @note
 * - Returns empty strings and false flags if no content is found
 * - Uses current locale and route context for content retrieval
 */
export const useInfoReturn = async (
  deliveryKey: string,
  categoriesSlugs: string[]
) => {
  const localePath = useLocalePath()
  const { country } = useRouteHelper()

  const query = {
    locale: localePath(''),
    vse: useRoute().query.vse,
    contentId: useRoute().query.contentId,
    deliveryKey,
  }

  const { data } = await useFetch('/api/cms/getInfoReturn', {
    query,
  })

  const title = data.value?.content.title ?? ''

  const countries =
    data.value?.content.countries?.map(
      country => (country as unknown as { code: string }).code
    ) ?? []

  const hideInCountry = countries.includes(country)

  const categories =
    data.value?.content.categories
      ?.map(category => (category as unknown as { urlSlug: string }).urlSlug)
      .filter(isNonNullable) ?? []

  const hideInCategory = categories.some(item => categoriesSlugs.includes(item))

  return { title, hideInCountry, hideInCategory }
}
